import { useEffect, useRef } from "react";
import {
  getDateRange,
  formatDateString,
  findTodaySectionIndex,
} from "../../../../../services/consults-utils";
import { trackEvent } from "../../../../../utils/mixpanel";
import type { ConsultSection } from "./useConsultSearch";

interface EffectsProps {
  state: any;
  dateRange: any;
  dummyData: any;
  data: any;
  navigation: any;
  session: any;
  tipsEnabled: boolean;
}

/**
 * Consolidates all useEffect hooks for consults
 */
export const useConsultEffects = ({
  state,
  dateRange,
  dummyData,
  data,
  navigation,
  session,
  tipsEnabled,
}: EffectsProps) => {
  // Track previous values to detect actual changes (not initial mount)
  const prevDateRangeRef = useRef<{ start: string; end: string } | null>(null);
  const prevDummyDataRef = useRef<boolean | null>(null);

  // Track page view on mount
  useEffect(() => {
    const { startDate, endDate } = getDateRange("weekly");
    const initialStartDate = formatDateString(startDate);
    const initialEndDate = formatDateString(endDate);

    trackEvent(
      "Consults Page Viewed",
      {
        "Date Range": `${formatDateString(startDate)} to ${formatDateString(endDate)}`,
        "View Mode": state.currentView,
        "View Type": dateRange.viewLabel,
      },
      session,
    );

    dateRange.setCurrentDateRange({
      start: initialStartDate,
      end: initialEndDate,
    });
  }, []);

  // Update date range when view mode changes
  useEffect(() => {
    const { startDate, endDate } = getDateRange(
      dateRange.viewMode,
      dateRange.rangeOffset,
    );
    const newStartDate = formatDateString(startDate);
    const newEndDate = formatDateString(endDate);

    dateRange.setCurrentDateRange({
      start: newStartDate,
      end: newEndDate,
    });
  }, [dateRange.viewMode, dateRange.rangeOffset]);

  // Track view changes
  useEffect(() => {
    if (state.currentView === "planningView" && state.selectedPatient) {
      trackEvent(
        "Planning Page Viewed",
        {
          "Patient ID": state.selectedPatient.clinicOSUUID,
          "Patient Name": state.selectedPatient.name,
          "Procedure Type": state.selectedPatient.procedureType,
          "Visit Type": state.selectedPatient.visitType || "Consultation",
          "Has Photos": state.selectedPatient.hasPhotos,
        },
        session,
      );

      state.setVoiceNoteRefreshKey((prev: number) => prev + 1);
    } else if (
      state.currentView === "presentationView" &&
      state.selectedPatient
    ) {
      trackEvent(
        "Presentation Page Viewed",
        {
          "Patient ID": state.selectedPatient.clinicOSUUID,
          "Patient Name": state.selectedPatient.name,
          "Procedure Type": state.selectedPatient.procedureType,
          "Visit Type": state.selectedPatient.visitType || "Consultation",
          "Has Photos": state.selectedPatient.hasPhotos,
          "Simulation Count": state.simulations?.length || 0,
        },
        session,
      );
    }
  }, [state.currentView, state.selectedPatient]);

  // Refresh schedule when date range changes (skip on initial mount)
  useEffect(() => {
    const currentRange = {
      start: dateRange.currentDateRange.start,
      end: dateRange.currentDateRange.end,
    };

    // Skip if this is the first time we're seeing values (initial mount)
    if (prevDateRangeRef.current === null) {
      console.log("⏭️ Skipping date range refresh on initial mount");
      prevDateRangeRef.current = currentRange;
      return;
    }

    // Only refresh if the values actually changed
    if (
      prevDateRangeRef.current.start !== currentRange.start ||
      prevDateRangeRef.current.end !== currentRange.end
    ) {
      console.log("📅 Date range changed, refreshing schedule");
      prevDateRangeRef.current = currentRange;
      data.refreshSchedule(currentRange.start, currentRange.end);
    }
  }, [dateRange.currentDateRange.start, dateRange.currentDateRange.end]);

  // Auto-create dummy data if needed
  useEffect(() => {
    if (
      dummyData.useDummyData &&
      !data.isLoading &&
      data.scheduleData?.length === 0
    ) {
      dummyData
        .createDummyDataIfNeeded(
          data.scheduleData,
          data.isLoading,
          data.totalAppointments,
        )
        .then((created: boolean) => {
          if (created) {
            data.refreshSchedule(
              dateRange.currentDateRange.start,
              dateRange.currentDateRange.end,
            );
          }
        });
    }
  }, [
    dummyData.useDummyData,
    data.isLoading,
    data.scheduleData?.length,
    session,
    dummyData.hasAttemptedDummyDataCreation,
  ]);

  // Refresh when dummy data toggle changes (skip on initial mount)
  useEffect(() => {
    // Skip if this is the first time we're seeing a value (initial mount)
    if (prevDummyDataRef.current === null) {
      console.log("⏭️ Skipping dummy data refresh on initial mount");
      prevDummyDataRef.current = dummyData.useDummyData;
      return;
    }

    // Only refresh if the value actually changed
    if (prevDummyDataRef.current !== dummyData.useDummyData) {
      console.log(
        `🔄 Refreshing data due to dummy data toggle: ${dummyData.useDummyData}`,
      );
      prevDummyDataRef.current = dummyData.useDummyData;

      if (dateRange.currentDateRange.start && dateRange.currentDateRange.end) {
        data.refreshSchedule(
          dateRange.currentDateRange.start,
          dateRange.currentDateRange.end,
        );
      }
    }
  }, [dummyData.useDummyData]);

  // Scroll to today in weekly view
  useEffect(() => {
    const shouldScroll =
      dateRange.viewMode === "weekly" &&
      dateRange.rangeOffset === 0 &&
      data.scheduleData &&
      data.scheduleData.length > 0 &&
      !data.isLoading &&
      data.scheduleData.some(
        (section: ConsultSection) => section.data && section.data.length > 0,
      );

    if (shouldScroll) {
      const timer = setTimeout(() => {
        if (state.scrollRef.current) {
          const todaySectionIndex = findTodaySectionIndex(data.scheduleData);
          if (todaySectionIndex !== -1) {
            try {
              state.scrollRef.current.scrollToLocation({
                sectionIndex: todaySectionIndex,
                itemIndex: 0,
                animated: true,
                viewOffset: 0,
              });
            } catch (err) {
              console.error("Error scrolling to section:", err);
            }
          }
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [
    dateRange.viewMode,
    dateRange.rangeOffset,
    data.scheduleData?.length,
    data.isLoading,
  ]);

  // Close range picker when navigating away from base view
  useEffect(() => {
    if (state.currentView !== "baseView" && state.rangePickerVisible) {
      state.setRangePickerVisible(false);
    }
  }, [state.currentView, state.rangePickerVisible]);
};
